import type { UseFormReturnType } from "@mantine/form"

import { queryClient } from "~/backend/queryClient"
import type { ResponseConfig } from "~/backend/axiosClient"
import {
  findBusinessRelationshipsQueryKey,
  findContactsByCreatedByIdQueryOptions,
  type FindBusinessRelationshipsItemDto,
  type FindBusinessRelationshipsQueryResponse,
} from "~/gen/api"
import type { CreateBusinessPartnerResponseDto } from "~/gen/api/types/CreateBusinessPartnerResponseDto.ts"

export type CreatedBusinessPartnerSelection = {
  readonly businessRelationshipId: string
  readonly displayName: string
  readonly organizationId: string
}

export const createdBusinessPartnerSelectionFromResult = (
  result: CreateBusinessPartnerResponseDto
): CreatedBusinessPartnerSelection => ({
  businessRelationshipId: result.businessRelationshipId,
  displayName: result.organizationName,
  organizationId: result.organizationId,
})

export const resolveCreatedBusinessPartnerSelection = async ({
  contactId,
  organizationId,
  optimisticName,
}: {
  readonly contactId: string
  readonly organizationId: string
  readonly optimisticName: string
}): Promise<CreatedBusinessPartnerSelection | null> => {
  const contactsResponse = await queryClient.fetchQuery(
    findContactsByCreatedByIdQueryOptions(organizationId)
  )
  const createdContact = contactsResponse.data.find(
    (item) => item.id === contactId
  )
  const businessRelationshipId =
    createdContact?.user.organization.businessRelationship.id
  if (businessRelationshipId === undefined) {
    return null
  }

  return {
    businessRelationshipId,
    displayName: createdContact?.user.organization.name ?? optimisticName,
    organizationId:
      createdContact?.user.organization.businessRelationship
        .supplierOrganizationId ?? "",
  }
}

export const applyCreatedBusinessPartnerSelection = ({
  form,
  fieldName,
  selection,
  createdBusinessPartnerRef,
}: {
  readonly form: UseFormReturnType<Record<string, unknown>>
  readonly fieldName: string
  readonly selection: CreatedBusinessPartnerSelection
  readonly createdBusinessPartnerRef: {
    current: { id: string; name: string } | null
  }
}): void => {
  const { businessRelationshipId, displayName, organizationId } = selection
  const ownerOrganizationId = organizationId.trim()
  if (ownerOrganizationId.length === 0) {
    return
  }

  createdBusinessPartnerRef.current = {
    id: ownerOrganizationId,
    name: displayName,
  }
  form.setFieldValue(fieldName, ownerOrganizationId)
  form.validateField(fieldName)
  const optimisticItem: FindBusinessRelationshipsItemDto = {
    id: businessRelationshipId,
    name: displayName,
    supplierOrganizationId: ownerOrganizationId,
  }
  queryClient.setQueryData(
    findBusinessRelationshipsQueryKey(),
    (
      current:
        | ResponseConfig<FindBusinessRelationshipsQueryResponse>
        | undefined
    ) => {
      if (current?.data === undefined) {
        return {
          ...current,
          data: [optimisticItem],
        }
      }
      if (
        current.data.some(
          (item) => item.supplierOrganizationId === ownerOrganizationId
        )
      ) {
        return current
      }
      return {
        ...current,
        data: [...current.data, optimisticItem],
      }
    }
  )
  void queryClient.invalidateQueries({
    queryKey: findBusinessRelationshipsQueryKey(),
  })
}
